import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { copyObject } from 'common/copyObject';
import { useStore } from 'store/StoreContext';
import { useNext } from 'hooks/UseNext3_1To3_2';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/UserRegister3Validator1';
import { RegisterUserData } from 'store/register-user/RegisterUserData';
import { useSiteDisplayItems, SiteDisplayItemsExtend } from 'hooks/UseSiteDisplayItems';
import { createBirthYearSelectValues } from 'common/birthYearCreator';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import { convertHalfToFull, convertFullToHalf, convertFullKana } from 'common/CharacterConverter';
import { RoutePath } from 'consts/RoutePath';
import {
	MESSAGE_PASSWORD_ANNOTATION,
	POPOVER_MESSAGE_PASSWORD_ANNOTATION,
	TARGET_MESSAGE_PASSWORD_POPOVER,
} from 'utils/messages';
import { PartialdAnnotation } from 'components/molecules/partial-annotation/PartialAnnotation';

type Props = {};
export type RegisterUserForm = RegisterUserData & {
	password_confirm: string;
	birthYear: string;
	birthMonth: string;
	birthDate: string;
};

export const UserRegister3Template1 = memo((_props: Props) => {
	const [store, dispatch] = useStore();
	const history = useHistory<SiteDisplayItemsExtend>();
	const { displayItems, isRequired, isOptional, isNone, getSiteDisplayItemLoading } = useSiteDisplayItems(
		store,
		history.location.state,
	);
	const registerUserData = copyObject(store.registerUserData);
	const birth = new Date(registerUserData.birth);
	const isBirthNaN = isNaN(birth.getFullYear());
	const [formData, setFormData] = useState<RegisterUserForm>({
		password_confirm: '',
		birthYear: !isBirthNaN ? birth.getFullYear().toString() : '',
		birthMonth: !isBirthNaN ? (birth.getMonth() + 1).toString() : '',
		birthDate: !isBirthNaN ? birth.getDate().toString() : '',
		...registerUserData,
	});
	const isMen = formData.sex === 0;
	const isWomen = formData.sex === 1;
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [processLoading, setProcessLoading] = useState(false);
	const { next } = useNext(
		formErrors,
		setFormErrors,
		getErrorMessage,
		formData,
		dispatch,
		displayItems,
		actionError,
		setActionError,
		setProcessLoading,
	);

	// パスワード注釈
	const splitText = new RegExp('(' + TARGET_MESSAGE_PASSWORD_POPOVER + ')');
	const annotationMessage = MESSAGE_PASSWORD_ANNOTATION.split(splitText);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleChangeRadioValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: Number(event.target.value) }));
	};
	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.checked ? 1 : 0 }));
	};
	const [isPlaceHolders, setIsPlaceHolders] = useState({
		birthYear: !formData.birthYear,
		birthMonth: !formData.birthMonth,
		birthDate: !formData.birthDate,
	});
	const handleChangeSelectValueWithPlaceHolder = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setIsPlaceHolders((prev) => ({ ...prev, [event.target.name]: !event.target.value }));
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleBlurConvertHalfToFullValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertHalfToFull(event.target.value) }));
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};
	const handleBlurConvertFullKanaValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertFullKana(event.target.value) }));
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		if (!store.registerUserData.mailaddress_1) {
			console.error(`error: mailaddress not exists.`);
			history.push(RoutePath.ERROR);
		}
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID新規登録｜本会員登録';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<Backdrop className={classes.backdrop} open={getSiteDisplayItemLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="steps">
						<img className="pc" src="images/img-steps-03.svg" alt="本会員登録" />
						<img className="sp" src="images/img-steps-03_sp.svg" alt="" />
					</div>
					<div className="step-title sp">本会員登録</div>
					<p>
						下記登録フォームに必要事項を入力し、「確認画面へ」ボタンを押してください。
						<br />
						本会員登録内容確認画面に移動します。
					</p>

					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.gid !== '' ? <p>【GakkenID】{formErrors.gid}</p> : null}
										{formErrors.pass !== '' ? <p>【パスワード】{formErrors.pass}</p> : null}
										{formErrors.password_confirm !== '' ? (
											<p>【パスワード（確認）】{formErrors.password_confirm}</p>
										) : null}
										{formErrors.lastname !== '' || formErrors.firstname !== '' ? (
											<p>【お名前】{formErrors.lastname || formErrors.firstname}</p>
										) : null}
										{formErrors.kana_lastname !== '' || formErrors.kana_firstname !== '' ? (
											<p>【お名前（カナ）】{formErrors.kana_lastname || formErrors.kana_firstname}</p>
										) : null}
										{formErrors.birthYear !== '' || formErrors.birthMonth !== '' || formErrors.birthDate !== '' ? (
											<p>【生年月日】{formErrors.birthYear || formErrors.birthMonth || formErrors.birthDate}</p>
										) : null}
										{formErrors.sex !== '' ? <p>【性別】{formErrors.sex}</p> : null}
									</div>
								</>
							) : null}
						</div>
					) : null}

					{isNone(displayItems.gid) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="GakkenID">GakkenID</label>
								{isRequired(displayItems.gid) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.gid) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※半角英数8文字以上</span>
							</div>
							<div className="form-input width50">
								<input
									id="GakkenID"
									type="text"
									name="gid"
									placeholder="gakken1946"
									value={formData.gid}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullToHalfValue}
								/>
							</div>
							<p className="error">{formErrors.gid}</p>
						</div>
					)}

					{isNone(displayItems.pass) ? null : (
						<div className="form-box">
							<div className={`form-head ${classes.displayFlex}`}>
								<div className={classes.newPasswordLabelBox}>
									<div className={classes.newPasswordLabel}>
										<label htmlFor="password">パスワード</label>
										{isRequired(displayItems.pass) ? <span className="required">必須</span> : null}
										{isOptional(displayItems.pass) ? <span className="optional">（任意）</span> : null}
									</div>
								</div>
								<div>
									<span className="annotation">
										<PartialdAnnotation
											headerMessage={annotationMessage[0]}
											footerMessage={annotationMessage[2]}
											targetMessage={annotationMessage[1]}
											popoverMessage={POPOVER_MESSAGE_PASSWORD_ANNOTATION}
											isBreakBeforeTargetText={true}
										/>
									</span>
								</div>
							</div>
							<div className="form-input width50">
								<input
									id="password"
									type="password"
									name="pass"
									placeholder="ABCdef123"
									value={formData.pass}
									onChange={handleChangeValue}
								/>
							</div>
							<p className="error">{formErrors.pass}</p>
						</div>
					)}

					{isNone(displayItems.pass) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="passwordConfirm">パスワード（確認）</label>
								{isRequired(displayItems.pass) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.pass) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※大文字、小文字、数字を含む半角8文字以上</span>
							</div>
							<div className="form-input width50">
								<input
									id="passwordConfirm"
									type="password"
									name="password_confirm"
									placeholder="ABCdef123"
									value={formData.password_confirm}
									onChange={handleChangeValue}
								/>
							</div>
							<p className="error">{formErrors.password_confirm}</p>
						</div>
					)}

					{isNone(displayItems.lastname) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="lastname">お名前（全角）</label>
								{isRequired(displayItems.lastname) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.lastname) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角</span>
							</div>
							<div className="form-input width50">
								<input
									id="lastname"
									type="text"
									name="lastname"
									placeholder="姓"
									value={formData.lastname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.lastname}</p>
							<div className="form-input width50">
								<input
									type="text"
									name="firstname"
									placeholder="名"
									value={formData.firstname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertHalfToFullValue}
								/>
							</div>
							<p className="error">{formErrors.firstname}</p>
						</div>
					)}

					{isNone(displayItems.kana_lastname) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="namekana">お名前（カナ）</label>
								{isRequired(displayItems.kana_lastname) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.kana_lastname) ? <span className="optional">（任意）</span> : null}
								<span className="annotation">※全角カタカナ</span>
							</div>
							<div className="form-input width50">
								<input
									id="namekana"
									type="text"
									name="kana_lastname"
									placeholder="セイ"
									value={formData.kana_lastname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullKanaValue}
								/>
							</div>
							<p className="error">{formErrors.kana_lastname}</p>
							<div className="form-input width50">
								<input
									type="text"
									name="kana_firstname"
									placeholder="メイ"
									value={formData.kana_firstname}
									onChange={handleChangeValue}
									onBlur={handleBlurConvertFullKanaValue}
								/>
							</div>
							<p className="error">{formErrors.kana_firstname}</p>
						</div>
					)}

					{isNone(displayItems.birth) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="birthYear">生年月日</label>
								{isRequired(displayItems.birth) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.birth) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="birthYear"
										name="birthYear"
										aria-label="年"
										className={isPlaceHolders.birthYear ? classes.placeholderOption : ''}
										value={formData.birthYear}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											年
										</option>
										{createBirthYearSelectValues().map((v) => {
											return (
												<option value={`${v}`} className={classes.defaultOption}>
													{v}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.birthYear}</p>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="birthMonth"
										name="birthMonth"
										aria-label="月"
										className={isPlaceHolders.birthMonth ? classes.placeholderOption : ''}
										value={formData.birthMonth}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											月
										</option>
										{new Array(12).fill(0).map((v, i) => {
											return (
												<option value={`${i + 1}`} className={classes.defaultOption}>
													{i + 1}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.birthMonth}</p>
							<div className="form-input width50">
								<div className="select-box">
									<select
										id="birthDate"
										name="birthDate"
										aria-label="日"
										className={isPlaceHolders.birthDate ? classes.placeholderOption : ''}
										value={formData.birthDate}
										onChange={handleChangeSelectValueWithPlaceHolder}
									>
										<option value="" className={classes.placeholderOption}>
											日
										</option>
										{new Array(31).fill(0).map((v, i) => {
											return (
												<option value={`${i + 1}`} className={classes.defaultOption}>
													{i + 1}
												</option>
											);
										})}
									</select>
								</div>
							</div>
							<p className="error">{formErrors.birthDate}</p>
						</div>
					)}

					{isNone(displayItems.sex) ? null : (
						<div className="form-box">
							<div className="form-head">
								<label htmlFor="male">性別</label>
								{isRequired(displayItems.sex) ? <span className="required">必須</span> : null}
								{isOptional(displayItems.sex) ? <span className="optional">（任意）</span> : null}
							</div>
							<div className="form-input">
								<div className="radio-box">
									<ul>
										<li>
											<label className={`radio-label${isMen ? ' is-active' : ''}`} htmlFor="male">
												<input
													type="radio"
													id="male"
													name="sex"
													value={0}
													checked={isMen}
													onChange={handleChangeRadioValue}
												/>
												男
											</label>
										</li>
										<li>
											<label className={`radio-label${isWomen ? ' is-active' : ''}`} htmlFor="female">
												<input
													type="radio"
													id="female"
													name="sex"
													value={1}
													checked={isWomen}
													onChange={handleChangeRadioValue}
												/>
												女
											</label>
										</li>
									</ul>
								</div>
							</div>
							<p className="error">{formErrors.sex}</p>
						</div>
					)}

					<div className="form-box">
						<div className="form-input">
							<div className="check-box">
								<ul>
									{isNone(displayItems.directmail_flg) ? null : (
										<li>
											<label
												className={`check-label ${formData.directmail_flg === 1 ? 'is-active' : ''}`}
												htmlFor="receive"
											>
												<input
													type="checkbox"
													id="receive"
													name="directmail_flg"
													checked={formData.directmail_flg === 1}
													onChange={handleChangeCheckValue}
												/>
												GakkenIDのメルマガを受け取る
											</label>
										</li>
									)}
								</ul>
							</div>
						</div>
					</div>

					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={next}>確認画面へ</button>}
						</div>
					</div>
				</div>
			</div>
		</main>
	);
});
